// lib/firebase.ts

import AsyncStorage from "@react-native-async-storage/async-storage";
import { getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  getReactNativePersistence,
  initializeAuth,
  type Auth,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { getStorage } from "firebase/storage";
import { Platform } from "react-native";

import { parseFirebaseRuntimeBinding } from "@/lib/firebaseRuntimeBinding";

function requireFirebaseConfigValue(name: string, value: string | undefined) {
  const configuredValue = value?.trim();

  const valid =
    name === "EXPO_PUBLIC_FIREBASE_API_KEY"
      ? /^AIza[A-Za-z0-9_-]{35}$/.test(configuredValue ?? "")
      : name === "EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID"
        ? /^[0-9]{6,20}$/.test(configuredValue ?? "")
        : name === "EXPO_PUBLIC_FIREBASE_APP_ID"
          ? /^1:[0-9]{6,20}:(?:web|android|ios):[A-Fa-f0-9]{12,64}$/.test(
              configuredValue ?? ""
            )
          : false;

  if (
    !valid ||
    /^(?:undefined|null|void\s+0|placeholder|change-?me|example|your[-_])/i.test(
      configuredValue ?? ""
    )
  ) {
    throw new Error(`Invalid required Firebase configuration: ${name}.`);
  }

  return configuredValue as string;
}

const optionalMeasurementId =
  process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID?.trim();

// Keep the parsed binding and both Firebase SDK initializers in this module so
// the exported runtime instances have one auditable data-flow graph.
const firebaseRuntimeBinding = parseFirebaseRuntimeBinding(
  process.env.EXPO_PUBLIC_SOKOGRID_FIREBASE_BINDING
);

const firebaseConfig = Object.freeze({
  apiKey: requireFirebaseConfigValue(
    "EXPO_PUBLIC_FIREBASE_API_KEY",
    process.env.EXPO_PUBLIC_FIREBASE_API_KEY
  ),
  authDomain: `${firebaseRuntimeBinding.frontendProjectId}.firebaseapp.com`,
  projectId: firebaseRuntimeBinding.frontendProjectId,
  storageBucket: firebaseRuntimeBinding.storageBucket,
  messagingSenderId: requireFirebaseConfigValue(
    "EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID",
    process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID
  ),
  appId: requireFirebaseConfigValue(
    "EXPO_PUBLIC_FIREBASE_APP_ID",
    process.env.EXPO_PUBLIC_FIREBASE_APP_ID
  ),
  ...(optionalMeasurementId ? { measurementId: optionalMeasurementId } : {}),
});

const existingDefaultApp = getApps().find(
  (candidate) => candidate.name === "[DEFAULT]"
);

if (
  existingDefaultApp &&
  (existingDefaultApp.options.projectId !==
    firebaseRuntimeBinding.frontendProjectId ||
    existingDefaultApp.options.storageBucket !==
      firebaseRuntimeBinding.storageBucket)
) {
  throw new Error(
    "Firebase project mismatch. Reload the app with the intended public Firebase configuration."
  );
}

export const app = initializeApp(firebaseConfig);

export const db = getFirestore(app);
export const storage = getStorage(app);
export const listingPaymentFunctions = getFunctions(
  app,
  firebaseRuntimeBinding.functionsRegion
);

let authInstance: Auth;

/**
 * On native, initializeAuth gives persistent login using AsyncStorage.
 * During Expo fast refresh, initializeAuth can throw if Auth already exists,
 * so we safely fall back to getAuth(app).
 */
if (Platform.OS === "web") {
  authInstance = getAuth(app);
} else {
  try {
    authInstance = initializeAuth(app, {
      persistence: getReactNativePersistence(AsyncStorage),
    });
  } catch {
    authInstance = getAuth(app);
  }
}

export const auth = authInstance;
